import { Transition } from '@headlessui/react'
import { ChangeEvent, Dispatch } from 'react'
import { AppAction, AppState } from '../appState'
import { getInitialPersonalLoanAmount } from '../lib/calculationUtils'
import CustomInput from './CustomInput'
import CustomSelect from './CustomSelect'
import { EventUnionType, getField, getValue } from './MortgageCalculator'
import MinusIcon from './icons/MinusIcon'
import PlusIcon from './icons/PlusIcon'
import { TypographyH2, TypographyP } from './typographyComponents'

interface Props {
  appState: AppState
  appDispatch: Dispatch<AppAction>
  show: boolean
}

function PersonalLoan({ appState, appDispatch, show }: Readonly<Props>) {
  function handleOnClick() {
    if (!appState.personalLoanIsAdded) {
      const value = getInitialPersonalLoanAmount(
        appState.propertyValue,
        appState.downPayment,
        appState.personalLoanTerms.minAmountForRedemptionOfLoan
      )
      appDispatch({
        type: 'fieldChange',
        field: 'personalLoanAmount',
        value,
      })
    }
    appDispatch({
      type: 'personalLoanIsAdded',
      value: !appState.personalLoanIsAdded,
    })
  }

  function onChangeHandler(event: EventUnionType) {
    const field = getField(event)
    if (field !== 'personalLoanAmount') return appDispatch({ type: 'error' })
    appDispatch({ type: 'fieldChange', field, value: getValue(event) })
  }

  function personalLoanLengthInYearsOnChangeHandler(
    event: ChangeEvent<HTMLSelectElement>
  ) {
    const field = event.currentTarget.id
    if (field !== 'personalLoanLengthInYears')
      return appDispatch({ type: 'error' })
    appDispatch({ type: 'fieldChange', field, value: +event.target.value })
  }

  return (
    <Transition
      show={show}
      appear={true}
      enter="transition-all duration-500"
      enterFrom="opacity-0 max-h-[0vh]"
      enterTo="opacity-100 max-h-[100vh]"
      leave="transition-all duration-500"
      leaveFrom="opacity-100 max-h-[100vh]"
      leaveTo="opacity-0 max-h-[0vh]"
    >
      <section
        className="border-2 border-neutral-80 p-[14px]"
        data-cy="personal-loan-section"
      >
        {appState.personalLoanIsAdded ? (
          <>
            <TypographyH2>Privatlån</TypographyH2>
            <div className="flex flex-wrap">
              <CustomInput
                id="personalLoanAmount"
                label="Privatlån"
                value={appState.personalLoanAmount}
                inputChangeHandler={onChangeHandler}
                suffix="kr"
                maxLength={7}
                useSlider
                min={appState.personalLoanTerms.minAmountForRedemptionOfLoan}
                max={appState.personalLoanTerms.maxPersonalLoanAmount}
              />
              <CustomSelect
                id="personalLoanLengthInYears"
                label="Återbetalningstid"
                value={appState.personalLoanLengthInYears}
                selectChangeHandler={personalLoanLengthInYearsOnChangeHandler}
              />
            </div>
            <button
              className="flex underline"
              onClick={handleOnClick}
              data-cy="remove-personal-loan-btn"
            >
              <MinusIcon className="mr-1" />
              <span>Ta bort privatlån</span>
            </button>
          </>
        ) : (
          <>
            <TypographyH2>
              Få ihop till kontantinsatsen med ett privatlån
            </TypographyH2>
            <TypographyP type="base">
              Har du svårt att få ihop pengar till kontantinsats kan du ta ett
              privatlån. För privatlån står inte bostaden som säkerhet för lånet
              och räntan bestäms separat. Hos oss kan du låna mellan 30 000 och
              500 000 kronor.
            </TypographyP>
            <button
              className="flex underline"
              onClick={handleOnClick}
              data-cy="add-personal-loan-btn"
            >
              <PlusIcon className="mr-1" />
              <span>Lägg till ett privatlån</span>
            </button>
          </>
        )}
      </section>
    </Transition>
  )
}

export default PersonalLoan
